import {
  createPatch,
  patchOpNames,
  type EmbeddedPatchPath,
  type PatchModel,
  type PatchOpName,
  type PatchOps,
  type PatchPath,
  type PatchableRecord,
} from "./patch"

type EmbeddedScope = "embedded"
type OpArgs<Value = unknown> = [path: PatchPath, value: Value]
type EmbeddedOpArgs<Value = unknown> = [scope: EmbeddedScope, path: PatchPath, value: Value]

export default class PatchUpdater {
  static create(
    modelType: string,
    id: string,
    embeddedPath?: EmbeddedPatchPath,
    embeddedElementMatch?: PatchableRecord,
  ): PatchUpdater {
    return new PatchUpdater(createPatch(modelType, id, embeddedPath, embeddedElementMatch))
  }

  private readonly patchModel: PatchModel

  private constructor(patch: PatchModel) {
    this.patchModel = patch
  }

  get patch(): PatchModel {
    const embeddedDocument = this.patchModel.embeddedDocument

    return {
      ...this.patchModel,
      id: null,
      document: {
        ...this.patchModel.document,
        ops: stripEmptyOps(this.patchModel.document.ops),
      },
      embeddedDocument:
        embeddedDocument === null
          ? null
          : {
              ...embeddedDocument,
              ops: stripEmptyOps(embeddedDocument.ops),
            },
      actionId: null,
      timeAtCreation: null,
      revisionAtCreation: null,
    }
  }

  get wrapInArray(): PatchModel[] {
    return [this.patch]
  }

  isCreatePatch(): this {
    this.patchModel.isCreatePatch = true
    return this
  }

  set(...args: OpArgs | EmbeddedOpArgs): this {
    return this.addOp("set", args)
  }

  unset(...args: OpArgs | EmbeddedOpArgs): this {
    return this.addOp("unset", args)
  }

  inc(...args: OpArgs<number> | EmbeddedOpArgs<number>): this {
    return this.addOp("inc", args)
  }

  push(...args: OpArgs | EmbeddedOpArgs): this {
    return this.addOp("push", args)
  }

  pull(...args: OpArgs | EmbeddedOpArgs): this {
    return this.addOp("pull", args)
  }

  pullAll(...args: OpArgs<unknown[]> | EmbeddedOpArgs<unknown[]>): this {
    return this.addOp("pullAll", args)
  }

  addToSet(...args: OpArgs | EmbeddedOpArgs): this {
    return this.addOp("addToSet", args)
  }

  private addOp(opName: PatchOpName, args: OpArgs | EmbeddedOpArgs): this {
    if (args.length === 3) {
      const [, path, value] = args

      if (this.patchModel.embeddedDocument === null) {
        throw new Error(`Cannot add embedded ${opName} patch without an embedded document.`)
      }

      const ops = this.patchModel.embeddedDocument.ops[opName] ?? []
      this.patchModel.embeddedDocument.ops[opName] = ops
      ops.push([path, value])
      return this
    }

    const [path, value] = args
    const ops = this.patchModel.document.ops[opName] ?? []
    this.patchModel.document.ops[opName] = ops
    ops.push([path, value])
    return this
  }
}

function stripEmptyOps(ops: PatchOps): PatchOps {
  const stripped: PatchOps = {}

  for (const opName of patchOpNames) {
    const opEntries = ops[opName]
    if (opEntries && opEntries.length > 0) {
      stripped[opName] = opEntries.slice()
    }
  }

  return stripped
}

export type { OpsDoc, PatchDocument, PatchEmbeddedDocument, PatchModel } from "./patch"
